/**
 * Gates on a weapon's own **runtime state**: is it recharged, is it loaded, has its
 * `Limited {N}` pool run dry.
 */

import type { EncounterState } from "@lancer-vtt/domain";
import { findAffordance, hasStatus, isWeaponLoaded, selectWeaponProfile, thrownWeaponObjectId, weaponInstanceKey } from "@lancer-vtt/domain";
import { ok, err } from "@lancer-vtt/shared";
import { selectWeapon } from "../resolve";
import { stateSelectedProfile, type LoadedOverlay } from "../effects/loader";
import { checkActionAvailable } from "./actionAvailability";
import type { BudgetVerdict } from "./types";
import { affordable } from "../events/gameEvents";

/**
 * Is `weapon` (as {@link selectWeapon} would resolve it) currently **usable** on `attacker` — not
 * sitting **discharged** after a Recharge-tagged fire (RAW Core p.281)? A `tg_recharge`-tagged
 * weapon that has fired stays discharged until a start-of-turn 1d6 recharge roll (`tickRecharge`,
 * `backend/src/encounterRoom.ts`) clears it — rejected here, before any dice, the same shape as
 * every other per-shot legality wall in this module. A weapon carrying no Recharge tag
 * (`weapon.recharge` absent/0 — every mech weapon and most NPC weapons) is never gated. This is
 * the **resolve-path half** of the shared usability predicate — the chooser-greying half reads
 * the same flag in the frontend.
 *
 * **The flag is read off `current.affordanceState`, the one per-affordance ledger** (`decisions/
 * 0176` ruling 1; `affordance-table` Phase 2.1) — weapons key into it by
 * {@link weaponInstanceKey}, granted actions by their affordance key, and "used or discharged" has
 * one shape regardless of what the thing is. The write is `applyResolved`'s `discharge_set` case,
 * which writes this ledger and nothing else (`affordance-table` Phase 3.1).
 */
export type CheckWeaponRechargeArgs = {
  state: EncounterState;
  attacker: string;
  /** The named weapon id off the intent (else the attacker's first frozen weapon / fallback). */
  weapon?: string;
  /** The firing-profile selection off the intent (a multi-profile weapon). */
  profile?: string;
};

export function checkWeaponRecharge(args: CheckWeaponRechargeArgs): BudgetVerdict {
  const { state, attacker, weapon, profile } = args;
  const atk = state.combatants[attacker];
  if (!atk) return ok(undefined); // a missing attacker is caught by the range gate alongside this.
  const w = selectWeapon(atk, weapon, profile);
  if (!w.recharge) return ok(undefined);
  const key = weaponInstanceKey(w);
  if (!affordable({ kind: "weapon", carrier: attacker, key, spend: "discharge" }, state, undefined)) {
    return err(`${w.name} is discharged (Recharge ${w.recharge}+) — awaits ${atk.name}'s next start-of-turn recharge roll`);
  }
  return ok(undefined);
}

/**
 * May the intent name this **firing profile**? A weapon whose overlay entry carries a `select`
 * clause has its profile set by state (`selectProfileByState`): the Assault Cannon fires Spin-Up
 * Mode only while spun up and Standard only while not (RAW: Cease Spin-Up "allow[s] the Assault
 * Cannon to be used with its Standard profile"). A named `profile` that differs from the
 * state-selected one is refused here, before any dice. An omitted `profile` always passes: the
 * resolver then fires the state-selected profile itself (`firingProfile`). A weapon without a
 * `select` clause is never gated.
 */
export type CheckProfileByStateArgs = CheckWeaponRechargeArgs & { overlay: LoadedOverlay };

export function checkProfileByState(args: CheckProfileByStateArgs): BudgetVerdict {
  const { state, attacker, weapon, profile, overlay } = args;
  const atk = state.combatants[attacker];
  if (!atk || profile === undefined) return ok(undefined);
  const base = selectWeapon(atk, weapon, undefined);
  const active = stateSelectedProfile({ state, actorId: attacker, weapon: base, overlay });
  if (active === undefined) return ok(undefined);
  const named = selectWeaponProfile(base, profile);
  // Every multi-profile weapon in lancer-data carries its numbers only on `profiles`, so the base
  // weapon is frozen from its first profile (`freezeWeaponProfile`): the two ids name one mode.
  const mode = (id: string) => (id === base.id ? (base.profiles?.[0]?.id ?? id) : id);
  if (mode(named.id) === mode(active.id)) return ok(undefined);
  return err(`${base.name} can't fire ${named.name} now: its state selects ${active.name}`);
}

/**
 * The tech-attack sibling of {@link checkWeaponRecharge}: a granted tech attack
 * (`base.techAttackActions`) whose feature carries `tg_recharge {N}` — Predatory Logic, Petrify —
 * is refused while its affordance sits discharged. The fire discharges it through
 * `techAttackProfile`'s `recharge` (`resolve/weapons.ts`), keyed on the same affordance key
 * `request_activate` discharges and {@link import("./carriedActions").checkGrantedActionLegality}
 * gates on, so firing via either row spends the one charge.
 */
export function checkTechAttackRecharge(args: { state: EncounterState; attacker: string; weapon?: string }): BudgetVerdict {
  const { state, attacker, weapon } = args;
  const atk = state.combatants[attacker];
  if (!atk || weapon === undefined || atk.base.techAttackActions?.[weapon] === undefined) return ok(undefined);
  const recharge = findAffordance(atk.base.affordances, weapon)?.recharge;
  if (recharge === undefined || !atk.current.affordanceState?.[weapon]?.discharged) return ok(undefined);
  return err(`${weapon}: ${atk.name} is discharged (Recharge ${recharge}+) — awaits ${atk.name}'s next start-of-turn recharge roll`);
}

/**
 * Is `weapon` (as {@link selectWeapon} would resolve it) currently **loaded** on `attacker`? A
 * `tg_loading`-tagged weapon that fired stays unloaded until a mech's Stabilize reload secondary
 * (`stabilize.ts`'s `case "reload"`) or a pilot's own Reload action (`reload.ts`) clears it —
 * rejected here, before any dice, the same shape as {@link checkWeaponRecharge} (its Recharge
 * analogue). A
 * weapon carrying no Loading tag (`weapon.loading` absent/false — the common case) is never
 * gated; {@link isWeaponLoaded}'s `?? 1` default makes this true unconditionally even without the
 * `weapon.loading` short-circuit, but the short-circuit keeps the rejection message scoped to
 * weapons that actually declare the tag. Rung into `resolveIntent`'s pre-dice ladder for a
 * standard attack and an Overwatch reaction (`backend/src/encounterRoom.ts`); Barrage's per-shot
 * walls below consult {@link isWeaponLoaded} directly (their `weapon` is already an instance key,
 * no `selectWeapon` needed). `gm_force` is a SEPARATE intent that skips this whole gate
 * (never a flag).
 *
 * The same wall refuses a **thrown weapon still downrange** (`tg_thrown`): its object
 * ({@link thrownWeaponObjectId}) stands beside the target it was thrown at until its owner
 * retrieves it, and RAW bars any use of the weapon until then. Every caller of this gate (the
 * attack and Overwatch ladders, Barrage's fire block, a forced-attack pick, a held Prepare, the AI's
 * legality) gets it for free.
 */
export type CheckLoadedArgs = {
  state: EncounterState;
  attacker: string;
  /** The named weapon id off the intent (else the attacker's first frozen weapon / fallback). */
  weapon?: string;
  /** The firing-profile selection off the intent (a multi-profile weapon). */
  profile?: string;
};

export function checkLoaded(args: CheckLoadedArgs): BudgetVerdict {
  const { state, attacker, weapon, profile } = args;
  const atk = state.combatants[attacker];
  if (!atk) return ok(undefined); // a missing attacker is caught by the range gate alongside this.
  const w = selectWeapon(atk, weapon, profile);
  const thrown = state.combatants[thrownWeaponObjectId(attacker, weaponInstanceKey(w))];
  if (thrown !== undefined && !hasStatus(thrown.statuses, "Destroyed")) {
    return err(`${w.name} was thrown and lies downrange — ${atk.name} must retrieve it (a free action while adjacent) before using it again`);
  }
  if (!w.loading) return ok(undefined);
  const key = weaponInstanceKey(w);
  if (!isWeaponLoaded(atk.current.resources, key)) {
    return err(`${w.name} is not loaded (Loading) — reload it (Stabilize for a mech, Reload for a pilot) before firing again`);
  }
  return ok(undefined);
}

/**
 * Is `weapon` (as {@link selectWeapon} would resolve it) currently **within its `Limited {N}`
 * charge count** on `attacker`? A `tg_limited`-tagged weapon that has fired `N` times is out of
 * charges for the REST OF THE ENCOUNTER (scene = encounter — no in-encounter reset) — rejected
 * here, before any dice, the same shape as {@link checkWeaponRecharge}/{@link checkLoaded}.
 * Delegates to {@link checkActionAvailable}'s `"limited"` grain (the shared read predicate, the
 * "one door" rule) rather than reading `current.resources` directly — this function is only the
 * weapon-selection + error-message wrapper around that one door, exactly like `checkLoaded` wraps
 * {@link isWeaponLoaded}. A weapon carrying no Limited tag (`weapon.limited` undefined — the
 * common case) is never gated.
 */
export type CheckLimitedArgs = {
  state: EncounterState;
  attacker: string;
  /** The named weapon id off the intent (else the attacker's first frozen weapon / fallback). */
  weapon?: string;
  /** The firing-profile selection off the intent (a multi-profile weapon). */
  profile?: string;
};

export function checkLimited(args: CheckLimitedArgs): BudgetVerdict {
  const { state, attacker, weapon, profile } = args;
  const atk = state.combatants[attacker];
  if (!atk) return ok(undefined); // a missing attacker is caught by the range gate alongside this.
  const w = selectWeapon(atk, weapon, profile);
  if (w.limited === undefined) return ok(undefined);
  const key = weaponInstanceKey(w);
  const verdict = checkActionAvailable({ state, actor: attacker, actionId: key, grain: "limited", max: w.limited });
  if (!verdict.ok) {
    return err(`${w.name} is out of charges (Limited ${w.limited}) — depleted for the rest of the encounter`);
  }
  return ok(undefined);
}

/** The refusal for a `noAttack` weapon (the Autopod): it fires only through its own reaction. */
export function noAttackRefusal(weapon: { name: string }): string {
  return `attack: ${weapon.name} can't be attacked with directly — only its reaction fires it`;
}
